import React from 'react';
import { Assets, Colors, FontFamily, Input, PrimaryButton } from 'utils/imports.utils';
import { ButtonSize, ButtonTypes } from 'utils/interface.utils';
import FileUpload from 'common_components/ui/file_upload/flie_upload.ui';
import { STRINGS } from 'constants/strings.constant';
import { SupportTicketRow } from 'interfaces/support.interface';
import { AccountSupportTabProps } from 'interfaces/account_settings.interface';
import './account_support_tab.component.scss';

const darkOutline = { border: `1.5px solid ${Colors.ink}`, color: Colors.ink, backgroundColor: Colors.white };

const formatTicketDate = (createdAt?: string): string =>
  createdAt ? ` · ${new Date(createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : '';

const NewTicketButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button type="button" className="account_support_new" onClick={onClick}>
    <span aria-hidden="true">+</span> {STRINGS.accountSettings.support.newTicket}
  </button>
);

const AccountSupportTab: React.FC<AccountSupportTabProps> = ({
  view,
  tickets,
  isLoading,
  subject,
  description,
  attachment,
  isSubmitting,
  onNewTicket,
  onBack,
  onSubjectChange,
  onDescriptionChange,
  onAttachmentUpload,
  onAttachmentSizeError,
  onAttachmentTypeError,
  onSubmit,
}) => {
  const text = STRINGS.accountSettings.support;
  return (
    <div className="account_support">
      <h2 className="account_settings_panel_heading">{text.title}</h2>
      <p className="account_settings_panel_subheading">{text.subtitle}</p>
      {view === 'list' && tickets.length === 0 && !isLoading && (
        <div className="account_support_empty">
          <div className="account_support_empty_icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={Colors.ink} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 11.5a8.4 8.4 0 0 1-12.2 7.5L3 20.5l1.6-5.2A8.4 8.4 0 1 1 21 11.5z" />
              <path d="M8.5 11.5h.01M12 11.5h.01M15.5 11.5h.01" strokeWidth="2.2" />
            </svg>
          </div>
          <h3 className="account_support_empty_title">{text.emptyTitle}</h3>
          <p className="account_support_empty_text">{text.emptyText}</p>
          <NewTicketButton onClick={onNewTicket} />
        </div>
      )}
      {view === 'list' && (tickets.length > 0 || isLoading) && (
        <div className="account_support_list">
          <div className="account_support_list_toolbar">
            <NewTicketButton onClick={onNewTicket} />
          </div>
          {tickets.map((ticket: SupportTicketRow) => (
            <div key={ticket._id} className="account_support_row">
              <div className="account_support_row_text">
                <div className="account_support_row_subject">{ticket.subject}</div>
                <div className="account_support_row_meta">
                  {ticket.ticket_number}
                  {formatTicketDate(ticket.created_at)}
                </div>
              </div>
              <span className="account_support_row_status">{(ticket.status ?? '').replace('_', ' ')}</span>
            </div>
          ))}
        </div>
      )}
      {view === 'new' && (
        <div className="account_support_form">
          <Input value={subject} label={text.subject} placeholder={text.subjectPlaceholder} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSubjectChange(e.target.value)} />
          <Input
            value={description}
            label={text.description}
            placeholder={text.descriptionPlaceholder}
            isTextArea
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onDescriptionChange(e.target.value)}
          />
          <div className="account_support_attachment">
            <div className="account_support_attachment_label">{text.attachmentLabel}</div>
            <div className="account_support_attachment_tile">
              <div className="account_support_attachment_name">{attachment ? attachment.name : text.attachmentEmpty}</div>
              <FileUpload
                fileTypes={['JPEG', 'JPG', 'PNG', 'WEBP']}
                maxFileSize={10 * 1024 * 1024}
                handleChange={onAttachmentUpload}
                handleSizeError={onAttachmentSizeError}
                handleTypeError={onAttachmentTypeError}>
                <PrimaryButton
                  onClick={() => {}}
                  text={attachment ? text.replaceImage : text.uploadImage}
                  type={ButtonTypes.OUTLINED}
                  size={ButtonSize.SMALL}
                  fontFamily={FontFamily.medium}
                  left_icon={Assets.camera}
                  iconSize={{ width: '16px', height: '16px' }}
                  style={darkOutline}
                />
              </FileUpload>
            </div>
          </div>
          <div className="account_support_buttons">
            <PrimaryButton text={text.back} type={ButtonTypes.OUTLINED} size={ButtonSize.MEDIUM} fontFamily={FontFamily.medium} onClick={onBack} style={darkOutline} />
            <PrimaryButton
              text={text.submit}
              type={ButtonTypes.PRIMARY_GRADIENT}
              size={ButtonSize.MEDIUM}
              fontFamily={FontFamily.medium}
              lineHeight="20px"
              activity={isSubmitting}
              onClick={onSubmit}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSupportTab;
